import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import { CDN_URL, getInlineLoader } from "../Helpers";

export default function LogoSlider() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  // Initialize data fetching following the standard pattern
  async function init() {
    setReady(false);
    if (typeof appContext?.getAllClients === "function") {
      await appContext.getAllClients();
    }
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  // Helper to resolve relative CDN URLs vs base64/absolute links safely
  const getImageUrl = (path) => {
    if (!path) return "";
    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:")
    ) {
      return path;
    }
    const baseUrl = (CDN_URL || "http://localhost:5000").replace(/\/+$/, "");
    const cleanPath = path.replace(/^\/+/, "");
    return `${baseUrl}/${cleanPath}`;
  };

  // Process clients list with dynamic image fallback
  const clientList = useMemo(() => {
    const rawClients = Array.isArray(appContext?.allClients)
      ? appContext.allClients
      : Array.isArray(appContext?.clients)
      ? appContext.clients
      : [];

    return rawClients
      .map((client) => {
        const rawImg = client?.logo || client?.coverPhoto || client?.image || "";
        return {
          id: client?.id || client?.uuid || client?.name,
          name: client?.name || client?.title || "Client Logo",
          img: getImageUrl(rawImg),
        };
      })
      .filter((client) => Boolean(client.img)); // Exclude clients without logos
  }, [appContext?.allClients, appContext?.clients]);

  // Double the list for infinite marquee effect
  const slides = useMemo(() => [...clientList, ...clientList], [clientList]);

  if (!ready) {
    return (
      <div className="slider-container text-center py-5">
        {getInlineLoader()}
      </div>
    );
  }

  if (clientList.length === 0) {
    return null; // Gracefully hide slider if no client logos exist
  }

  return (
    <div className="slider-container">
      {/* Header Section */}
      <div className="slider-header">
        <span className="subtitle">50+ Happy Customers</span>
        <h2 className="main-title">
          Powering Innovation with{" "}
          <span className="gradient-text">Top Tier Brands</span>
        </h2>
        <div className="title-underline"></div>
      </div>

      <div className="slider-content">
        <div className="fade-overlay left"></div>
        <div className="fade-overlay right"></div>

        <div className="track">
          {slides.map((client, index) => (
            <div className="slide" key={`${client.id}-${index}`}>
              <div className="glass-card">
                <img src={client.img} alt={client.name} loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .slider-container {
          width: 100%;
          background: #0a0a0a;
          padding: 6rem 0;
          overflow: hidden;
        }

        /* Header Styling */
        .slider-header {
          text-align: center;
          margin-bottom: 4rem;
          padding: 0 20px;
        }

        .subtitle {
          display: block;
          color: var(--secondaryColor, #38bdf8);
          font-size: 1.25rem;
          font-weight: 800;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .main-title {
          color: #ffffff;
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 800;
          margin: 0 auto;
          max-width: 900px;
          line-height: 1.2;
        }

        .gradient-text {
          background: linear-gradient(
            135deg,
            var(--secondaryColor, #38bdf8),
            var(--primaryColor, #6366f1)
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .title-underline {
          width: 80px;
          height: 4px;
          background: linear-gradient(
            90deg,
            var(--secondaryColor, #38bdf8),
            transparent
          );
          margin: 1.5rem auto 0;
          border-radius: 2px;
        }

        /* Infinite Slider Track */
        .slider-content {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .fade-overlay {
          position: absolute;
          top: 0;
          width: 200px;
          height: 100%;
          z-index: 2;
          pointer-events: none;
        }
        .left {
          left: 0;
          background: linear-gradient(to right, #0a0a0a, transparent);
        }
        .right {
          right: 0;
          background: linear-gradient(to left, #0a0a0a, transparent);
        }

        .track {
          display: flex;
          width: calc(250px * ${slides.length});
          animation: scroll 40s linear infinite;
        }

        .track:hover {
          animation-play-state: paused;
        }

        .slide {
          width: 250px;
          flex-shrink: 0;
          padding: 0 15px;
          display: flex;
          justify-content: center;
        }

        /* Glassmorphism Card */
        .glass-card {
          width: 100%;
          height: 120px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 25px;
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
        }

        .glass-card img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          transition: all 0.4s ease;
        }

        .glass-card:hover {
          background: rgba(255, 255, 255, 0.05);
          transform: translateY(-10px);
          border-color: var(--secondaryColor, #38bdf8);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
        }

        .glass-card:hover img {
          opacity: 1;
          transform: scale(1.05);
        }

        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-250px * ${clientList.length}));
          }
        }

        /* Responsive Breakpoints */
        @media (max-width: 768px) {
          .slider-container {
            padding: 4rem 0;
          }
          .slide {
            width: 180px;
          }
          .glass-card {
            height: 100px;
            padding: 18px;
          }
          .fade-overlay {
            width: 100px;
          }
          .track {
            width: calc(180px * ${slides.length});
          }
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-180px * ${clientList.length}));
            }
          }
        }
      `}</style>
    </div>
  );
}